import { forwardRef, useMemo } from 'react';
import {
  MolecularSVGProvider,
  SVGCanvas,
  applyZoomToViewBox,
  parseViewBoxSafe,
  viewBoxToString,
  HTMLStructureTree,
  buildStructure,
  buildAltTextMapFromStructure,
  collectHiddenAtomIds,
} from '@alchemiesolns/svg-interactive';
import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { fromConfigString } from '@alchemiesolns/workspace-serialization';
import { extractWorkspaceFeatures } from '@alchemiesolns/feature-extractor-compat';
import type { ArrowType } from './ArrowSection';
import { ReactionArrow } from './ReactionArrow';
import { AtomScaleWrap } from './AtomScaleWrap';
import { InteractiveMolecule } from './InteractiveMolecule';
import {
  SCHEME_DISPLAY_ZOOM_DEFAULT,
  SCHEME_DISPLAY_ZOOM_MAX,
  SCHEME_DISPLAY_ZOOM_MIN,
} from '../constants/reactionScheme';
import { DEFAULT_REACTION_EXPORT_DIMENSIONS, REACTION_LAYOUT_BASE } from '../utils/exportLayout';
import {
  computeReactionStageLayout,
  computeReferenceReactionStageLayout,
} from '../utils/reactionExportLayout';
import {
  REACTION_LABEL_BASE_PX,
  REACTION_PREVIEW_FONT_FAMILY,
  reactionPreviewDropTargetHeadingStyle,
} from '../constants/previewTypography';
import { OverlayTextSvg, type OverlayTextHorizontalAlign } from './OverlayTextSvg';
import { useTapClickSelectSkippingDrag } from '../hooks/useTapClickSelectSkippingDrag';
import { schemeViewBoxZoomFactorForApply } from '../utils/schemeZoom';

/**
 * Build an accessibility description for the reaction arrow.
 * Describes the arrow type and any reagents/conditions shown above/below.
 */
function buildArrowDescription(
  arrowType: 'single' | 'double',
  reagents: string,
  conditions: string
): string {
  const typeLabel = arrowType === 'double' ? 'Resonance arrow' : 'Reaction arrow';
  if (reagents && conditions) {
    return `${typeLabel} with ${reagents} above and ${conditions} below`;
  } else if (reagents) {
    return `${typeLabel} with ${reagents} above`;
  } else if (conditions) {
    return `${typeLabel} with ${conditions} below`;
  }
  return typeLabel;
}

interface ExportableSchemeProps {
  reactantConfig: string;
  productConfig: string;
  topText: string;
  bottomText: string;
  arrowType: ArrowType;
  reactantViewBox?: string;
  productViewBox?: string;
  /** Layout fit viewBoxes (from normalization). Required for interactive preview; do not conflate with saved zoom state. */
  reactantLayoutViewBox?: string;
  productLayoutViewBox?: string;
  reactantPersistedViewBox?: string | null;
  productPersistedViewBox?: string | null;
  reactantOverlayText?: string;
  productOverlayText?: string;
  reactantOverlayPosition?: { x: number; y: number } | null;
  productOverlayPosition?: { x: number; y: number } | null;
  onReactantOverlayPositionChange?: (p: { x: number; y: number }) => void;
  onProductOverlayPositionChange?: (p: { x: number; y: number }) => void;
  reactantOverlayTextAlign?: OverlayTextHorizontalAlign;
  productOverlayTextAlign?: OverlayTextHorizontalAlign;
  reactantZoom?: number;
  /** When set (e.g. from parent), used for arrow + label sizing; else derived from viewBox + export size */
  arrowClusterScale?: number;
  /** Scheme zoom applied inside molecule SVGs and arrow column sizing (not CSS row scale). */
  schemeDisplayZoom?: number;
  exportWidth?: number;
  exportHeight?: number;
  showReactantDropTarget?: boolean;
  showProductDropTarget?: boolean;
  onReactantDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onReactantDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  onProductDragOver?: (e: React.DragEvent<HTMLDivElement>) => void;
  onProductDrop?: (e: React.DragEvent<HTMLDivElement>) => void;
  onReactantPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onProductPaste?: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  selectedReactant?: boolean;
  selectedProduct?: boolean;
  selectedArrow?: boolean;
  onReactantSelect?: () => void;
  onProductSelect?: () => void;
  onReactantDoubleClick?: () => void;
  onProductDoubleClick?: () => void;
  onArrowSelect?: () => void;
  interactivePreview?: boolean;
  onReactantViewBoxChange?: (viewBox: string | null) => void;
  onProductViewBoxChange?: (viewBox: string | null) => void;
  onReactantZoomChange?: (zoom: number) => void;
  onProductZoomChange?: (zoom: number) => void;
  onReactantControlsReady?: (controls: unknown | null) => void;
  onProductControlsReady?: (controls: unknown | null) => void;
  /** Optional root attributes (e.g. build preview shell) */
  id?: string;
  className?: string;
}

interface BorderlessMoleculeProps {
  config: string;
  width: number;
  height: number;
  viewBoxOverride?: string;
  layoutViewBox?: string;
  /** Apply scheme zoom to the rendered viewBox (SVG-native scaling). */
  schemeDisplayZoom?: number;
  overlayText?: string;
  overlayPosition?: { x: number; y: number } | null;
  overlayInteractive?: boolean;
  onOverlayPositionChange?: (p: { x: number; y: number }) => void;
  overlayTextHorizontalAlign?: OverlayTextHorizontalAlign;
}

function BorderlessMolecule({
  config,
  width,
  height,
  viewBoxOverride,
  layoutViewBox,
  schemeDisplayZoom = SCHEME_DISPLAY_ZOOM_DEFAULT,
  overlayText,
  overlayPosition = null,
  overlayInteractive = false,
  onOverlayPositionChange,
  overlayTextHorizontalAlign = 'start',
}: BorderlessMoleculeProps) {
  const workspace = useMemo(() => {
    if (!config) return new MolecularWorkspace();
    try {
      return fromConfigString(config);
    } catch {
      return new MolecularWorkspace();
    }
  }, [config]);

  const resolvedViewBoxForCanvas = useMemo(() => {
    const src = viewBoxOverride ?? layoutViewBox;
    if (!src) return undefined;
    if (schemeDisplayZoom === 1) return src;
    const fit = parseViewBoxSafe(src);
    const z = schemeViewBoxZoomFactorForApply(schemeDisplayZoom);
    return viewBoxToString(
      applyZoomToViewBox(fit, z, 0.5, 0.5, fit, undefined, 8)
    );
  }, [viewBoxOverride, layoutViewBox, schemeDisplayZoom]);

  // Build structure for HTMLStructureTree (accessibility)
  const featureResult = useMemo(() => {
    if (!config || !workspace) return null;
    try {
      return extractWorkspaceFeatures(workspace);
    } catch {
      return null;
    }
  }, [config, workspace]);

  const structure = useMemo(() => {
    if (!workspace || !featureResult) return null;
    try {
      return buildStructure(workspace, featureResult);
    } catch {
      return null;
    }
  }, [workspace, featureResult]);

  // PIPH-132: Pass featureResult and workspace for functional group descriptions
  const treeLabelMap = useMemo(() => {
    if (!structure) return null;
    try {
      return buildAltTextMapFromStructure(structure, featureResult ?? undefined, workspace);
    } catch {
      return null;
    }
  }, [structure, featureResult, workspace]);

  // MC-886: filter hidden atoms out of the navigation tree.
  const hiddenAtomIds = useMemo(
    () => (workspace ? collectHiddenAtomIds(workspace) : undefined),
    [workspace]
  );

  // If overlay text exists, render text instead of molecule
  if (overlayText) {
    return (
      <OverlayTextSvg
        text={overlayText}
        width={width}
        height={height}
        fontFamily={REACTION_PREVIEW_FONT_FAMILY}
        horizontalAlign={overlayTextHorizontalAlign}
        position={overlayPosition}
        interactive={overlayInteractive && Boolean(onOverlayPositionChange)}
        onPositionChange={onOverlayPositionChange}
      />
    );
  }

  return (
    <MolecularSVGProvider
      workspace={workspace}
      svgWidth={width}
      svgHeight={height}
    >
      <SVGCanvas
        style={{ border: 'none' }}
        viewBoxOverride={resolvedViewBoxForCanvas}
        featureResult={featureResult ?? undefined}
      >
        {/* HTMLStructureTree for accessibility - rendered inside foreignObject in the SVG. */}
        {structure && treeLabelMap && (
          <HTMLStructureTree
            structure={structure}
            treeLabelMap={treeLabelMap}
            hiddenAtomIds={hiddenAtomIds}
            defaultExpandAll
          />
        )}
      </SVGCanvas>
    </MolecularSVGProvider>
  );
}

export const ExportableScheme = forwardRef<HTMLDivElement, ExportableSchemeProps>(
  ({
    reactantConfig,
    productConfig,
    topText,
    bottomText,
    arrowType,
    reactantViewBox,
    productViewBox,
    reactantLayoutViewBox,
    productLayoutViewBox,
    reactantPersistedViewBox,
    productPersistedViewBox,
    reactantOverlayText,
    productOverlayText,
    reactantOverlayPosition = null,
    productOverlayPosition = null,
    onReactantOverlayPositionChange,
    onProductOverlayPositionChange,
    reactantOverlayTextAlign = 'start',
    productOverlayTextAlign = 'start',
    reactantZoom = 1,
    arrowClusterScale: arrowClusterScaleProp,
    schemeDisplayZoom = SCHEME_DISPLAY_ZOOM_DEFAULT,
    exportWidth = DEFAULT_REACTION_EXPORT_DIMENSIONS.width,
    exportHeight = DEFAULT_REACTION_EXPORT_DIMENSIONS.height,
    showReactantDropTarget = false,
    showProductDropTarget = false,
    onReactantDragOver,
    onReactantDrop,
    onProductDragOver,
    onProductDrop,
    onReactantPaste,
    onProductPaste,
    selectedReactant = false,
    selectedProduct = false,
    selectedArrow = false,
    onReactantSelect,
    onProductSelect,
    onReactantDoubleClick,
    onProductDoubleClick,
    onArrowSelect,
    interactivePreview = false,
    onReactantViewBoxChange,
    onProductViewBoxChange,
    onReactantZoomChange,
    onProductZoomChange,
    onReactantControlsReady,
    onProductControlsReady,
    id: rootId,
    className: rootClassName,
  }, ref) => {
    const reactantTapSelect = useTapClickSelectSkippingDrag(onReactantSelect);
    const productTapSelect = useTapClickSelectSkippingDrag(onProductSelect);

    const stagePadding = REACTION_LAYOUT_BASE.padding;
    const stageGap = REACTION_LAYOUT_BASE.gap;

    /**
     * Reference slot size at default export dimensions — used only for atom-scale compensation
     * (match apparent bond size vs widening the canvas). Row layout uses `slotLayout` below.
     * Use the intrinsic layout fit viewBox, not the live pan/zoom viewport.
     */
    const referenceReactantViewBoxForStage = reactantLayoutViewBox ?? reactantViewBox;

    const referenceStageLayout = useMemo(
      () =>
        computeReferenceReactionStageLayout(
          topText,
          bottomText,
          arrowType,
          referenceReactantViewBoxForStage,
          reactantZoom,
          arrowClusterScaleProp
        ),
      [
        topText,
        bottomText,
        arrowType,
        referenceReactantViewBoxForStage,
        reactantZoom,
        arrowClusterScaleProp,
      ]
    );

    const schemeZoomClamped = Math.min(
      SCHEME_DISPLAY_ZOOM_MAX,
      Math.max(SCHEME_DISPLAY_ZOOM_MIN, schemeDisplayZoom)
    );

    const slotLayout = useMemo(
      () =>
        computeReactionStageLayout(
          { width: exportWidth, height: exportHeight },
          topText,
          bottomText,
          arrowType,
          referenceReactantViewBoxForStage,
          reactantZoom,
          arrowClusterScaleProp,
          schemeZoomClamped
        ),
      [
        exportWidth,
        exportHeight,
        topText,
        bottomText,
        arrowType,
        referenceReactantViewBoxForStage,
        reactantZoom,
        arrowClusterScaleProp,
        schemeZoomClamped,
      ]
    );

    const { arrowWidth, moleculeWidth: moleculeColumnWidth, contentHeight } = slotLayout;
    const effectiveLabelScale = schemeZoomClamped;
    const effectiveLabelFontPx = REACTION_LABEL_BASE_PX * effectiveLabelScale;

    /**
     * Keep at 1 so molecule SVGs fill `#reactant-molecule` / `#product-molecule` as export
     * width/height change. (A non-1 value preserves apparent bond size across resizes by
     * CSS-scaling the graphic smaller/larger inside the slot — opposite of “fill the div”.)
     */
    const moleculeSlotScale = 1;

    return (
      <div
        ref={ref}
        id={rootId}
        className={rootClassName}
        style={{
          width: exportWidth,
          height: exportHeight,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
        }}
      >
        <div
          id="molecule-stage"
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: exportWidth,
            height: exportHeight,
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            padding: stagePadding,
            backgroundColor: '#ffffff',
          }}
        >
          <div
            id="molecule-column"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              flexShrink: 0,
              gap: stageGap,
            }}
          >
          {/* Reactant molecule - no border */}
          <div
            id="reactant-molecule"
            className={`preview-paste-slot preview-paste-slot--reactant${selectedReactant ? ' preview-paste-slot--selected' : ''}`}
            style={{
              width: moleculeColumnWidth,
              height: contentHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
            tabIndex={0}
            onPointerDownCapture={reactantTapSelect.onPointerDownCapture}
            onClick={reactantTapSelect.onClickSkipDrag}
            onDoubleClick={() => onReactantDoubleClick?.()}
            onFocus={onReactantSelect}
            onDragOver={onReactantDragOver}
            onDrop={onReactantDrop}
            onPaste={onReactantPaste}
          >
            {(reactantConfig || reactantOverlayText) && (
              interactivePreview && reactantConfig && reactantLayoutViewBox && !reactantOverlayText ? (
                <AtomScaleWrap compensation={moleculeSlotScale} anchor="right">
                  <InteractiveMolecule
                    config={reactantConfig}
                    svgWidth={moleculeColumnWidth}
                    svgHeight={contentHeight}
                    layoutViewBox={reactantLayoutViewBox}
                    persistedViewBox={reactantPersistedViewBox ?? null}
                    schemeDisplayZoom={schemeZoomClamped}
                    interactionMode="sidebarOnly"
                    onViewBoxChange={onReactantViewBoxChange}
                    onZoomChange={onReactantZoomChange}
                    alignZoomControls="none"
                    ariaLabel="Reactant molecular structure with zoom and pan controls"
                    onControlsReady={onReactantControlsReady}
                  />
                </AtomScaleWrap>
              ) : (
                <AtomScaleWrap compensation={moleculeSlotScale} anchor="right">
                  <BorderlessMolecule
                    config={reactantConfig}
                    width={moleculeColumnWidth}
                    height={contentHeight}
                    viewBoxOverride={reactantViewBox}
                    layoutViewBox={reactantLayoutViewBox}
                    schemeDisplayZoom={schemeZoomClamped}
                    overlayText={reactantOverlayText}
                    overlayPosition={reactantOverlayPosition}
                    overlayInteractive={interactivePreview && Boolean(reactantOverlayText)}
                    onOverlayPositionChange={onReactantOverlayPositionChange}
                    overlayTextHorizontalAlign={reactantOverlayTextAlign}
                  />
                </AtomScaleWrap>
              )
            )}
            {showReactantDropTarget && !reactantConfig && !reactantOverlayText && (
              <div className="preview-slot-drop-target">
                <p
                  className="default-overlay-heading"
                  style={reactionPreviewDropTargetHeadingStyle(effectiveLabelFontPx)}
                >
                  Edit Reactant
                </p>
              </div>
            )}
          </div>

          {/* Arrow section with text labels - scaled by reactant zoom */}
          <div
            className={`preview-paste-slot preview-paste-slot--arrow${selectedArrow ? ' preview-paste-slot--selected' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: contentHeight,
            }}
            tabIndex={0}
            onClick={onArrowSelect}
            onFocus={onArrowSelect}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: `calc(${2 * effectiveLabelScale}rem + ${14 * effectiveLabelScale}px)`,
              }}
            >
              <span className="text-offset" style={{ paddingRight: arrowType === 'double' ? '0px' : `${15 * effectiveLabelScale}px`, height: `${1 * effectiveLabelScale}rem`, fontSize: `${effectiveLabelFontPx}px`, lineHeight: '1', fontFamily: REACTION_PREVIEW_FONT_FAMILY, whiteSpace: 'nowrap' }}>{topText}</span>
              <ReactionArrow
                arrowType={arrowType}
                width={arrowWidth}
                height={14 * effectiveLabelScale}
                strokeWidth={10 * effectiveLabelScale}
                ariaLabel={buildArrowDescription(arrowType, topText, bottomText)}
              />
              <span className="text-offset" style={{ paddingRight: arrowType === 'double' ? '0px' : `${15 * effectiveLabelScale}px`, height: `${1 * effectiveLabelScale}rem`, fontSize: `${effectiveLabelFontPx}px`, lineHeight: '1', fontFamily: REACTION_PREVIEW_FONT_FAMILY, whiteSpace: 'nowrap' }}>{bottomText}</span>
            </div>
          </div>

          {/* Product molecule - no border */}
          <div
            id="product-molecule"
            className={`preview-paste-slot preview-paste-slot--product${selectedProduct ? ' preview-paste-slot--selected' : ''}`}
            style={{
              width: moleculeColumnWidth,
              height: contentHeight,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
            tabIndex={0}
            onPointerDownCapture={productTapSelect.onPointerDownCapture}
            onClick={productTapSelect.onClickSkipDrag}
            onDoubleClick={() => onProductDoubleClick?.()}
            onFocus={onProductSelect}
            onDragOver={onProductDragOver}
            onDrop={onProductDrop}
            onPaste={onProductPaste}
          >
            {(productConfig || productOverlayText) && (
              interactivePreview && productConfig && productLayoutViewBox && !productOverlayText ? (
                <AtomScaleWrap compensation={moleculeSlotScale} anchor="left">
                  <InteractiveMolecule
                    config={productConfig}
                    svgWidth={moleculeColumnWidth}
                    svgHeight={contentHeight}
                    layoutViewBox={productLayoutViewBox}
                    persistedViewBox={productPersistedViewBox ?? null}
                    schemeDisplayZoom={schemeZoomClamped}
                    interactionMode="sidebarOnly"
                    onViewBoxChange={onProductViewBoxChange}
                    onZoomChange={onProductZoomChange}
                    alignZoomControls="none"
                    ariaLabel="Product molecular structure with zoom and pan controls"
                    onControlsReady={onProductControlsReady}
                  />
                </AtomScaleWrap>
              ) : (
                <AtomScaleWrap compensation={moleculeSlotScale} anchor="left">
                  <BorderlessMolecule
                    config={productConfig}
                    width={moleculeColumnWidth}
                    height={contentHeight}
                    viewBoxOverride={productViewBox}
                    layoutViewBox={productLayoutViewBox}
                    schemeDisplayZoom={schemeZoomClamped}
                    overlayText={productOverlayText}
                    overlayPosition={productOverlayPosition}
                    overlayInteractive={interactivePreview && Boolean(productOverlayText)}
                    onOverlayPositionChange={onProductOverlayPositionChange}
                    overlayTextHorizontalAlign={productOverlayTextAlign}
                  />
                </AtomScaleWrap>
              )
            )}
            {showProductDropTarget && !productConfig && !productOverlayText && (
              <div className="preview-slot-drop-target">
                <p
                  className="default-overlay-heading"
                  style={reactionPreviewDropTargetHeadingStyle(effectiveLabelFontPx)}
                >
                  Edit Product
                </p>
              </div>
            )}
          </div>
          </div>
        </div>
      </div>
    );
  }
);

ExportableScheme.displayName = 'ExportableScheme';
